<script setup lang="ts">
import { ref, computed, watch, onMounted, toRaw } from 'vue';
import axiosInstance from '../plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useAnswerStore } from '../store/answers';
import { useFlagStore } from '../store/flagStore';
import { determineNextQuestion } from '../assets/js/questionLogic';
import { evaluateGateKeeper } from '../assets/js/gateKeeperLogic'
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useUserVariablesStore } from '../store/userVariables';

const route = useRoute()

const currentLanguage = ref(route.query.lang)
const userVariablesStore = useUserVariablesStore();

const uiBlocked = ref(false)
const isAppLoading = ref(true);
const translation = computed(() => userVariablesStore.currentLanguageData);
const languageCodes = ref<string[]>([]);
const translationIsLoading = ref(true);
const evaluationIsLoading = ref(false);
const options = ref();
const question = ref();
const selectedCategory = ref();
const previousID = ref();
const op = ref();
const infoHTML = ref();
const warnMessage = ref();
const warnMessageVisible = ref(false);
const historyStack = ref([]);
const showEvaluation = ref(false);
const evalID = ref();
const router = useRouter();
const disabledOptions = ref({});
const evaluationDetails = ref({});
const answerStore = useAnswerStore();
const flagStore = useFlagStore();
const popoverText = ref('')
const helpVisible = ref(false)
const settings = ref([]);
const toast = useToast()
const activeTranslation = ref({});
const userRole = ref('');

const answers = computed(() => answerStore.answers);

const allFlagsSummary = computed(() => flagStore.getAllFlagsSummary);




const getQuestionDetails = async (questionId) => {
    try {
        const res = await axiosInstance.get(`/questions/${questionId}`);
        return { data: res.data, errorMessage: null };
    } catch (error) {
        return { data: null, errorMessage: `Error fetching question details: ${error.message}` };
    }
}


const toggle = (event: any, info: string) => {
    helpVisible.value = true
    popoverText.value = info;
};


const getSettings = async () => {
    const res = await axiosInstance.get('/settings');
    settings.value = res.data;
};

const getFirstQuestion = async () => {
    const resSettings = await axiosInstance.get('/settings');
    settings.value = resSettings.data;
    try {
        const res = await axiosInstance.get('/questions/withOptions/' + settings.value.startingQuestion);
        question.value = res.data.question;
        options.value = sortOptions(res.data.options);
    } catch (e) {
        console.error(e);
    }
};

const goTo = async (nextID: string) => {
    if (evaluationDetails.value) {
        evaluationIsLoading.value = true;
        evaluationDetails.value = {};
    }

    if (question.value && question.value._id) {
        historyStack.value.push(question.value._id);
    }

    previousID.value = question.value._id;
    try {
        selectedCategory.value = null;

        const res = await axiosInstance.get(`/questions/withOptions/${nextID}?lang=${currentLanguage.value}`);

        question.value = res.data.question;
        options.value = sortOptions(res.data.options);

        showEvaluation.value = false;
    } catch (e) {
        console.error(e);
    }
};



const goBack = async () => {
    if (historyStack.value.length > 0) {
        const previousId = historyStack.value.pop();
        try {
            selectedCategory.value = null;
            const res = await axiosInstance.get(`/questions/withOptions/${previousId}?lang=${currentLanguage.value}`);
            question.value = res.data.question;
            options.value = sortOptions(res.data.options);


            answerStore.removeLastAnswer();
            let revertedFlags = flagStore.goBack();

            if (revertedFlags.length && userRole.value.includes('ai_lab')) {
                revertedFlags.forEach(flag => {
                    toast.add({
                        severity: 'secondary',
                        summary: 'Flag Value Reverted',
                        detail: 'Flag Name: ' + flag.name + '\nOld Value: ' + flag.oldValue + '\nNew Value: ' + flag.newValue,
                        life: 7000
                    });
                });
            }
        } catch (e) {
            console.error(e);
        }
    }
};


const sortOptions = (opt) => {
    const sorted = opt.slice().sort((a, b) => {
        if (a.optionLetter < b.optionLetter) {
            return -1;
        }
        if (a.optionLetter > b.optionLetter) {
            return 1;
        }
        return 0;
    });
    return sorted;
};


const nextQuestion = async () => {

    if (!selectedCategory.value || selectedCategory.value.length === 0) {

        console.error('Keine Option ausgewählt');
        if (question.value.type === 'multipleChoice') {
            setWarnMessage(translation.value.staticTexts.selectAtLeastOneOption)
        } else {
            setWarnMessage(translation.value.staticTexts.selectOneOption)
        }
        return;
    }

    const selectedCategories = Array.isArray(selectedCategory.value) ? selectedCategory.value : [selectedCategory.value];
    const selectedOptions = options.value.filter(option => selectedCategories.includes(option.optionLetter));
    await saveAnswers(selectedOptions);

    const result = await determineNextQuestion(
        question.value,
        answers.value,
        allFlagsSummary.value
    );

    await processNextQuestion(result);

}



function setWarnMessage(message: string) {
    warnMessage.value = message;
    warnMessageVisible.value = true;
}

async function saveAnswers(selectedOptions) {
    const optionsArray = selectedOptions.map(option => ({
        id: option._id,
        title: option.title,
        identifier: option.optionLetter
    }));
    await answerStore.addAnswer(toRaw(question.value), toRaw(optionsArray));
}

const getFlagName = (flagId) => {
    let flag = allFlagsSummary.value.find(f => f.id === flagId);
    return flag.name
}


const processNextQuestion = async (initialResult) => {

    if (initialResult.changeFlags) {

        if (userRole.value.includes('ai_lab')) {
            initialResult.changeFlags.forEach(flag => {
                toast.add({ severity: 'info', summary: 'Flag Value Changed', detail: 'Flag Name: ' + getFlagName(flag.id) + '\nOld Value: ' + allFlagsSummary.value.find(f => f.id === flag.id).currentValue + '\nNew Value: ' + flag.newValue, life: 7000 });
            })
        }
        flagStore.updateFlags(initialResult.changeFlags);
    }
    if (!initialResult.changeFlags) {
        flagStore.updateFlags(null);
    }

    if (initialResult.nextQuestion && initialResult.nextQuestion.gateKeeper && initialResult.nextQuestion.gateKeeper.length) {
        const gateKeeperResult = await evaluateGateKeeper(
            initialResult.nextQuestion._id,
            answers.value,
            allFlagsSummary.value
        );

        if (gateKeeperResult.errorMessage) {
            throw new Error(gateKeeperResult.errorMessage);
        } else if (gateKeeperResult.nextFieldId) {
            const updatedQuestionDetails = await getQuestionDetails(gateKeeperResult.nextFieldId);
            if (updatedQuestionDetails.errorMessage) {
                throw new Error(updatedQuestionDetails.errorMessage);
            }
            initialResult.nextQuestion = updatedQuestionDetails.data;
        }
    }

    if (initialResult.errorMessage) {
        throw new Error(initialResult.errorMessage);
    } else if (initialResult.nextQuestion === 'evaluation') {
        evalID.value = '66890c94fac86fca8cb17cd8';
        showEvaluation.value = true;
        warnMessageVisible.value = false;
        const reportID = await flagStore.saveFlagsToDB();
        console.log(reportID);
        router.push('/results/' + reportID + '?lang=' + currentLanguage.value);

    } else if (initialResult.nextQuestion) {
        warnMessageVisible.value = false;
        await goTo(initialResult.nextQuestion._id);
    } else {
        throw new Error('Keine passende Folgefrage gefunden');
    }
}

const checkAndDisableOptions = () => {
    const selected = new Set(selectedCategory.value);
    const hasExcludes = options.value.some(o => selected.has(o.optionLetter) && o.excludesOtherOptions);

    for (const option of options.value) {
        disabledOptions.value[option.optionLetter] =
            selected.size > 0 &&
            (
                hasExcludes
                    ? !selected.has(option.optionLetter)
                    : option.excludesOtherOptions && !selected.has(option.optionLetter)
            );
    }
};



const isAuthenticated = ref(false);

watch(selectedCategory, checkAndDisableOptions, { deep: true });






const checkIfAuthenticated = async () => {
    const res = await axiosInstance.get('/user/isAuthenticated', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    isAuthenticated.value = res.data;
    if (!isAuthenticated.value) {
        localStorage.removeItem('token');
    }
    if (isAuthenticated.value) {
        const roleRes = await axiosInstance.get('/auth/user', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        })
        userRole.value = roleRes.data.user.role;
    }
}




    watch(() => route.query.lang, async (newLang: any) => {
        if (newLang && languageCodes.value.includes(newLang)) {
            currentLanguage.value = newLang;
            await userVariablesStore.loadLanguageData(newLang);
        }
    });


    watch(() => translation.value, async () => {
        if (warnMessageVisible.value) {
            if (question.value.type === 'multipleChoice') {
                setWarnMessage(translation.value.staticTexts.selectAtLeastOneOption)
            } else {
                setWarnMessage(translation.value.staticTexts.selectOneOption)
            }
        }
    });


    const initPage = async () => {
        await userVariablesStore.loadLanguageData(currentLanguage.value || defaultLanguage.value);
        languageCodes.value = userVariablesStore.allLanguageCodes;
        translationIsLoading.value = false;
    };


    onMounted(async () => {
        await getFirstQuestion();
        await getSettings();
        await initPage();

        userVariablesStore.setLanguage(currentLanguage.value || defaultLanguage.value);


        activeTranslation.value = translation.value[currentLanguage.value] || translation.value["en"];
        if (!route.query.lang) {
            router.push({ query: { ...route.query, lang: "de" } });
        }

        checkAndDisableOptions();
        await flagStore.initializeStore();

        Promise.all(Array.from(document.images).filter(img => !img.complete).map(img => new Promise(resolve => { img.onload = img.onerror = resolve; }))).then(() => {
            setTimeout(() => {
                isAppLoading.value = false;
            }, 300);
        });

        if (localStorage.token) {
            await checkIfAuthenticated();
        }
    });



</script>

<template>
    <div class="quiz-container">
        <div v-if="translation && translation.questions && question" class="quiz-wrapper">
            <transition name="slide-fade" mode="out-in">
                <div :key="question._id">
                    <div>
                        <BlockUI :blocked="uiBlocked" fullScreen />
                        <div class="question-title">
                            <Skeleton v-if="!translation" height="1rem" width="10rem"></Skeleton>
                            {{ translation.questions[question._id].title }}
                        </div>
                        <div class="question-text questionStyle">
                            <Skeleton v-if="!translation" height="2rem" width="30rem"></Skeleton>
                            {{ translation.questions[question._id].questionText }}
                            <Button v-if="translation.questions[question._id].questionHelp" severity="secondary"
                                outlined rounded class="p-button-sm ml-2 help-icon"
                                @click="toggle($event, translation.questions[question._id].questionHelp)">
                                <template #icon>
                                    <font-awesome-icon :icon="['fas', 'info']" />
                                </template>
                            </Button>
                            <Button v-if="translation.questions[question._id].lawText" severity="secondary" outlined
                                rounded class="p-button-sm ml-2 help-icon"
                                @click="toggle($event, translation.questions[question._id].lawText)">
                                <template #icon>
                                    <font-awesome-icon :icon="['fas', 'section']" />
                                </template>
                            </Button>
                        </div>
                        <Message class="mb-3" v-show="warnMessageVisible" severity="warn">{{ warnMessage }}</Message>
                        <div class="optionBox">
                            <div v-for="option in options" :key="option._id"
                                class="flex align-items-center optionContainer">
                                <div class="c-flex single-choice" v-if="question.type === 'singleChoice'">
                                    <Skeleton v-if="!translation" height="1rem" width="10rem"></Skeleton>
                                    <RadioButton class="controlElementColor" v-model="selectedCategory"
                                        :inputId="option._id" name="dynamic" :value="option.optionLetter" />
                                    <label :for="option._id" class="ml-2">
                                        {{ translation?.options[option._id]?.title }}
                                        <Button v-if="translation?.options[option._id]?.lawText" outlined rounded
                                            class="p-button-sm ml-2 help-icon"
                                            @click="toggle($event, translation?.options[option._id]?.lawText)">
                                            <template #icon>
                                                <font-awesome-icon :icon="['fas', 'section']" />
                                            </template>
                                        </Button>
                                        <Button v-if="translation?.options[option._id]?.info" severity="secondary"
                                            outlined rounded class="p-button-sm ml-2 help-icon"
                                            @click="toggle($event, translation?.options[option._id]?.info)">
                                            <template #icon>
                                                <font-awesome-icon :icon="['fas', 'info']" />
                                            </template>
                                        </Button>
                                    </label>
                                </div>
                                <div class="c-flex multiple-coice" v-if="question.type === 'multipleChoice'">
                                    <Skeleton v-if="!translation" height="1rem" width="10rem"></Skeleton>
                                    <Checkbox v-model="selectedCategory" :inputId="option._id" name="dynamic"
                                        :value="option.optionLetter" :disabled="disabledOptions[option.optionLetter]"
                                        @click="checkAndDisableOptions" />
                                    <label :for="option._id" class="m1-2">
                                        {{ translation.options[option._id].title }}
                                        <Button v-if="translation.options[option._id].lawText" outlined rounded
                                            class="p-button-sm ml-2 help-icon"
                                            @click="toggle($event, translation.options[option._id].lawText)">
                                            <template #icon>
                                                <font-awesome-icon :icon="['fas', 'section']" />
                                            </template>
                                        </Button>
                                        <Button v-if="translation.options[option._id].info" severity="secondary"
                                            outlined rounded class="p-button-sm ml-2 help-icon"
                                            @click="toggle($event, translation.options[option._id].info)">
                                            <template #icon>
                                                <font-awesome-icon :icon="['fas', 'info']" />
                                            </template>
                                        </Button>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>


                    <Dialog dismissableMask v-model:visible="helpVisible" header="Information"
                        :style="{ width: '25rem' }" position="top" :modal="true" :draggable="false">
                        <div class="flex items-center gap-4 mb-4">
                            <p v-html="popoverText"></p>
                        </div>
                    </Dialog>
                </div>
                <!-- Wrapper-Div Ende -->
            </transition>
        </div>
        <div v-else class="p-3">
            <Skeleton height="2rem" class="mb-2"></Skeleton>
            <Skeleton height="2rem" class="mb-2"></Skeleton>
            <Skeleton height="2rem" class="mb-2"></Skeleton>
        </div>
    </div>

    <!-- Quiz Buttons und Overlay Panel -->
    <div class="quiz-button-container row align-items-center justify-content-around">
        <Button v-if="historyStack.length && !showEvaluation && translation" class="quiz-button col-3" @click="goBack">
            {{ translation.staticTexts.backButton }}
        </Button>
        <Button v-if="!showEvaluation && translation" class="quiz-button col-3" @click="nextQuestion">
            {{ translation.staticTexts.nextButton }}
        </Button>
    </div>

    <OverlayPanel ref="op">
        <div class="flex flex-column gap-3 w-25rem">
            <div v-html="infoHTML" class="block"></div>
        </div>
    </OverlayPanel>
</template>



<style scoped>
.p-button-outlined {
    border-width: 1px !important;
    border-style: solid !important;
}

.controlElementColor .p-radiobutton-box {
    /* Your dynamic class styles */
    background-color: lightblue;
}
</style>